// FLAGS: identity
// Per-provider OIDC client settings for the SSO popup — authority, client id, redirect uri, scopes.
// Unknown providers return null rather than throwing, which the caller surfaces as a generic error.
import { env } from "@src/env";
import { SsoSettings } from "@src/models/SsoSettings";
import { SSO_PROVIDER } from "@src/utils/constants";
import {
  getPhaseTwoBaseDomain,
  getPhaseTwoLanguage,
} from "@src/utils/phaseTwoHosts";
import { getHostForLanguage } from "@src/utils/hostLanguage";
import i18n from "i18next";

// FLAGS: identity
// Both branches set disablePKCE, and the redirect_uri is built from the current locale domain — a domain not
// registered with the provider fails at the provider, not here. Changing scopes changes which claims the API sees.
export const getSsoSettings = (provider: string) => {
  const domain = getHostForLanguage(i18n.language);
  let ssoSettings: any;

  switch (provider.toLowerCase()) {
    case SSO_PROVIDER.Pwc.toLowerCase():
      ssoSettings = {
        authority: env.REACT_APP_PWC_AUTHORITY,
        client_id: env.REACT_APP_PWC_CLIENT_ID,
        redirect_uri: `https://${domain}/${env.REACT_APP_PWC_REDIRECT_URI}`,
        response_type: "code",
        disablePKCE: true,
        // Web authenticates against Entra, which rejects the PwC-specific scopes
        // (AADSTS650053). The PwC attribute claims (preferredMail, cloudEmail,
        // pwcLocalLOSLevel*, grade codes, ...) are emitted via the Entra app
        // registration's claim configuration, not requested as scopes. Mobile
        // still authenticates against OpenAM and keeps its own custom scopes.
        scope: "openid email profile",
      } as SsoSettings;
      break;

    case SSO_PROVIDER.Microsoft.toLowerCase():
      ssoSettings = {
        authority: env.REACT_APP_MICROSOFT_AUTHORITY,
        client_id: env.REACT_APP_MICROSOFT_CLIENT_ID,
        redirect_uri: domain
          ? `https://${domain}`
          : env.REACT_APP_MICROSOFT_REDIRECT_URI,
        response_type: "code",
        disablePKCE: true,
        scope: "openid profile email offline_access",
        prompt: "select_account",
      };
      break;

    case SSO_PROVIDER.PhaseTwo.toLowerCase(): {
      // The authority is the .com base, NOT the active locale domain. login.*
      // exists only there; deriving it from the active language built
      // login.openup-test.nl and friends, which resolve nowhere, so
      // createSigninRequest threw and the sign-in button did nothing at all for
      // four of five languages (OI-25494). Language goes via ui_locales instead.
      const baseDomain = getPhaseTwoBaseDomain();
      // No resolvable domain (an environment with no English domain mapped)
      // would otherwise build "https://login.null/..." and send the user to a
      // host that does not exist. Returning null is the contract getPhaseTwoRegisterUrl
      // already follows, and startPhaseTwoRedirect surfaces it via its onError.
      if (!baseDomain) {
        return null;
      }
      ssoSettings = {
        authority: `https://login.${baseDomain}/realms/${env.REACT_APP_PHASE_TWO_REALM}`,
        client_id: env.REACT_APP_PHASE_TWO_CLIENT_ID,
        // Deliberately the ACTIVE locale domain: this is what returns the user
        // to the site they started on. All five are registered redirect URIs on
        // the realm client. Do not "tidy" this to match the authority above.
        redirect_uri: `https://${domain}/${env.REACT_APP_PHASE_TWO_REDIRECT_URI}`,
        response_type: "code",
        disablePKCE: true,
        scope: "openid email profile",
        ui_locales: getPhaseTwoLanguage(),
      } as SsoSettings;
      break;
    }
    default:
      ssoSettings = null;
      break;
  }

  return ssoSettings;
};
